import React, { useEffect, useState, useMemo } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Plus, Trash2, Printer, FileCheck2, Ban, Eye, FileText } from "lucide-react";

const STATUS_COLOR = {
  draft: "slate", issued: "blue", paid: "green",
  partially_paid: "amber", cancelled: "red",
};

const CUSTOMER_TYPES = [
  "pharmacy","clinic","hospital","warehouse","wholesaler","distributor","doctor","government","other"
];

const newItem = () => ({
  product_id: "", product_name: "", sku: "", description: "",
  quantity: 1, unit_price: 0, discount_pct: 0, vat_rate: 14,
});

const emptyForm = {
  customer_id: "", customer_name: "", customer_type: "pharmacy",
  customer_tax_id: "", customer_commercial_reg: "", customer_address: "", customer_phone: "",
  items: [newItem()], payment_terms: "Net 30", due_date: "", notes: "",
  global_discount_pct: 0, issue_immediately: false,
};

export default function Invoices() {
  const { user } = useAuth();
  const has = (p) => user?.permissions?.includes(p);

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState({ status: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewOpen, setViewOpen] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [payOpen, setPayOpen] = useState(null);
  const [payAmount, setPayAmount] = useState(0);

  const load = async () => {
    const params = {};
    if (filter.status) params.status = filter.status;
    const [inv, c, p] = await Promise.all([
      api.get("/invoices", { params }),
      api.get("/customers"),
      api.get("/products"),
    ]);
    setItems(inv.data); setCustomers(c.data); setProducts(p.data);
  };
  useEffect(() => { load(); }, [filter.status]);

  const totals = useMemo(() => {
    let sub = 0, vat = 0;
    form.items.forEach((it) => {
      const q = parseFloat(it.quantity) || 0;
      const u = parseFloat(it.unit_price) || 0;
      const d = parseFloat(it.discount_pct) || 0;
      const v = parseFloat(it.vat_rate) || 0;
      const s = q * u * (1 - d / 100);
      sub += s; vat += s * (v / 100);
    });
    const gd = parseFloat(form.global_discount_pct) || 0;
    const subAfter = sub * (1 - gd / 100);
    const vatAfter = vat * (1 - gd / 100);
    return { subBefore: sub, gdAmt: sub - subAfter, subAfter, vat: vatAfter, grand: subAfter + vatAfter };
  }, [form.items, form.global_discount_pct]);

  const updateItem = (idx, key, val) => {
    const next = [...form.items];
    next[idx] = { ...next[idx], [key]: val };
    if (key === "product_id") {
      const p = products.find((pp) => pp.id === val);
      if (p) {
        next[idx].product_name = p.name;
        next[idx].sku = p.sku;
        next[idx].unit_price = p.pharmacy_price || 0;
        next[idx].vat_rate = p.vat_rate ?? 14;
      }
    }
    setForm({ ...form, items: next });
  };

  const onPickCustomer = (cid) => {
    const c = customers.find((cc) => cc.id === cid);
    setForm({
      ...form,
      customer_id: cid,
      customer_name: c?.name || "",
      customer_type: c?.customer_type || "pharmacy",
      customer_tax_id: c?.tax_registration_number || "",
      customer_commercial_reg: c?.commercial_registration_number || "",
      customer_address: c?.address || "",
      customer_phone: c?.phone || "",
    });
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      customer_id: inv.customer_id || "",
      customer_name: inv.customer_name || "",
      customer_type: inv.customer_type || "pharmacy",
      customer_tax_id: inv.customer_tax_id || "",
      customer_commercial_reg: inv.customer_commercial_reg || "",
      customer_address: inv.customer_address || "",
      customer_phone: inv.customer_phone || "",
      items: (inv.items || []).map((i) => ({
        product_id: i.product_id || "", product_name: i.product_name || "", sku: i.sku || "",
        description: i.description || "", quantity: i.quantity, unit_price: i.unit_price,
        discount_pct: i.discount_pct || 0, vat_rate: i.vat_rate || 14,
      })),
      payment_terms: inv.payment_terms || "Net 30",
      due_date: inv.due_date || "",
      notes: inv.notes || "",
      global_discount_pct: inv.global_discount_pct || 0,
      issue_immediately: false,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name?.trim()) { alert("Customer name is required"); return; }
    if (form.items.length === 0) { alert("Add at least one item"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        global_discount_pct: parseFloat(form.global_discount_pct) || 0,
        items: form.items.map((i) => ({
          ...i,
          product_id: i.product_id || null,
          quantity: parseFloat(i.quantity) || 0,
          unit_price: parseFloat(i.unit_price) || 0,
          discount_pct: parseFloat(i.discount_pct) || 0,
          vat_rate: parseFloat(i.vat_rate) || 0,
        })),
      };
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      setOpen(false);
      await load();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const issue = async (inv) => {
    if (!window.confirm(`Issue (finalize) tax invoice ${inv.number}? Once issued, it cannot be edited.`)) return;
    try {
      await api.put(`/invoices/${inv.id}/status`, { status: "issued" });
      await load();
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) { alert("Please provide a cancellation reason"); return; }
    try {
      await api.put(`/invoices/${cancelOpen.id}/status`, { status: "cancelled", cancel_reason: cancelReason });
      setCancelOpen(null); setCancelReason("");
      await load();
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  const submitPayment = async () => {
    const amt = parseFloat(payAmount) || 0;
    if (amt <= 0) { alert("Enter a positive amount"); return; }
    const newPaid = (payOpen.paid_amount || 0) + amt;
    const status = newPaid >= payOpen.grand_total ? "paid" : "partially_paid";
    try {
      await api.put(`/invoices/${payOpen.id}/status`, { status, paid_amount: newPaid });
      setPayOpen(null); setPayAmount(0);
      await load();
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  const openPdf = (inv) => {
    const token = localStorage.getItem("access_token");
    fetch(`${API_BASE}/invoices/${inv.id}/pdf`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b);
      window.open(u, "_blank");
    });
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Tax Invoices"
        subtitle="Official Egyptian tax-compliant invoices with VAT breakdown"
        actions={
          has("invoices.create") && (
            <Btn onClick={openCreate} data-testid="invoice-add-btn">
              <Plus className="w-4 h-4" /> New Invoice
            </Btn>
          )
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex gap-3 flex-wrap">
          <Select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="invoice-status-filter">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </div>
      </Card>

      {items.length === 0 ? (
        <EmptyState message="No tax invoices yet." />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-start">Invoice #</th>
                <th className="px-4 py-3 text-start">Customer</th>
                <th className="px-4 py-3 text-start">Type</th>
                <th className="px-4 py-3 text-start">Issued</th>
                <th className="px-4 py-3 text-start">Due</th>
                <th className="px-4 py-3 text-end">Total</th>
                <th className="px-4 py-3 text-end">Paid</th>
                <th className="px-4 py-3 text-start">Status</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-700 dark:text-blue-300">{inv.number}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{inv.customer_type}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">{inv.issued_at ? fmtDate(inv.issued_at) : <span className="text-slate-400">draft</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">{inv.due_date || "-"}</td>
                  <td className="px-4 py-3 text-end font-semibold">{fmtMoney(inv.grand_total)} EGP</td>
                  <td className="px-4 py-3 text-end font-mono text-emerald-600">{fmtMoney(inv.paid_amount || 0)}</td>
                  <td className="px-4 py-3"><Badge color={STATUS_COLOR[inv.status]}>{inv.status.replace("_", " ")}</Badge></td>
                  <td className="px-4 py-3 text-end">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setViewOpen(inv)} title="View" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`invoice-view-${inv.id}`}>
                        <Eye className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                      </button>
                      <button onClick={() => openPdf(inv)} title="Print/PDF" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`invoice-pdf-${inv.id}`}>
                        <Printer className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                      </button>
                      {has("invoices.issue") && inv.status === "draft" && (
                        <button onClick={() => issue(inv)} title="Issue (finalize)" className="p-1.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-950/40" data-testid={`invoice-issue-${inv.id}`}>
                          <FileCheck2 className="w-4 h-4 text-emerald-600" />
                        </button>
                      )}
                      {has("invoices.edit") && inv.status === "draft" && (
                        <button onClick={() => openEdit(inv)} title="Edit draft" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700">
                          <FileText className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </button>
                      )}
                      {has("invoices.edit") && ["issued", "partially_paid"].includes(inv.status) && (
                        <button onClick={() => { setPayOpen(inv); setPayAmount(0); }} title="Record payment" className="p-1.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-950/40" data-testid={`invoice-pay-${inv.id}`}>
                          <FileCheck2 className="w-4 h-4 text-emerald-600" />
                        </button>
                      )}
                      {has("invoices.cancel") && inv.status !== "cancelled" && inv.status !== "paid" && (
                        <button onClick={() => { setCancelOpen(inv); setCancelReason(""); }} title="Cancel" className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/40" data-testid={`invoice-cancel-${inv.id}`}>
                          <Ban className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit Invoice ${editing.number}` : "New Tax Invoice"} maxWidth="max-w-5xl">
        <form onSubmit={submit} className="space-y-4" data-testid="invoice-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select label="Existing Customer (optional)" value={form.customer_id} onChange={(e) => onPickCustomer(e.target.value)} data-testid="invoice-customer-select">
              <option value="">— Select to auto-fill —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.customer_type})</option>)}
            </Select>
            <Input label="Customer Name *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required data-testid="invoice-customer-name" />
            <Select label="Customer Type" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })}>
              {CUSTOMER_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
            </Select>
            <Input label="Tax Reg # (الرقم الضريبي)" value={form.customer_tax_id} onChange={(e) => setForm({ ...form, customer_tax_id: e.target.value })} />
            <Input label="Commercial Reg #" value={form.customer_commercial_reg} onChange={(e) => setForm({ ...form, customer_commercial_reg: e.target.value })} />
            <Input label="Phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
            <div className="md:col-span-3">
              <Input label="Address" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
            </div>
          </div>

          <div className="rounded-md ring-1 ring-slate-200 dark:ring-slate-700 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                <tr>
                  <th className="px-2 py-2 text-start">Product / Description</th>
                  <th className="px-2 py-2 text-end">Qty</th>
                  <th className="px-2 py-2 text-end">Unit Price</th>
                  <th className="px-2 py-2 text-end">Disc %</th>
                  <th className="px-2 py-2 text-end">VAT %</th>
                  <th className="px-2 py-2 text-end">Subtotal</th>
                  <th className="px-2 py-2 text-end">VAT</th>
                  <th className="px-2 py-2 text-end">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {form.items.map((it, i) => {
                  const q = parseFloat(it.quantity) || 0, u = parseFloat(it.unit_price) || 0;
                  const d = parseFloat(it.discount_pct) || 0, v = parseFloat(it.vat_rate) || 0;
                  const s = q * u * (1 - d / 100); const vt = s * (v / 100);
                  return (
                    <tr key={i} className="align-top">
                      <td className="px-2 py-2 min-w-[220px]">
                        <select
                          className="block w-full rounded border-slate-300 text-xs mb-1 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                          value={it.product_id}
                          onChange={(e) => updateItem(i, "product_id", e.target.value)}
                          data-testid={`invoice-item-product-${i}`}
                        >
                          <option value="">Select product or use custom text</option>
                          {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                        </select>
                        <input
                          className="block w-full rounded border-slate-300 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                          placeholder="Description (optional)"
                          value={it.product_name}
                          onChange={(e) => updateItem(i, "product_name", e.target.value)}
                        />
                      </td>
                      <td className="px-2 py-2 w-20"><input type="number" min="0" step="any" className="w-full rounded border-slate-300 text-end text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} /></td>
                      <td className="px-2 py-2 w-24"><input type="number" min="0" step="any" className="w-full rounded border-slate-300 text-end text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} /></td>
                      <td className="px-2 py-2 w-16"><input type="number" min="0" max="100" step="0.01" className="w-full rounded border-slate-300 text-end text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white" value={it.discount_pct} onChange={(e) => updateItem(i, "discount_pct", e.target.value)} /></td>
                      <td className="px-2 py-2 w-16"><input type="number" min="0" step="0.01" className="w-full rounded border-slate-300 text-end text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white" value={it.vat_rate} onChange={(e) => updateItem(i, "vat_rate", e.target.value)} /></td>
                      <td className="px-2 py-2 text-end font-mono">{fmtMoney(s)}</td>
                      <td className="px-2 py-2 text-end font-mono text-slate-500">{fmtMoney(vt)}</td>
                      <td className="px-2 py-2 text-end font-mono font-semibold">{fmtMoney(s + vt)}</td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })} className="text-red-600 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="p-2 border-t border-slate-200 dark:border-slate-700">
              <Btn type="button" variant="secondary" onClick={() => setForm({ ...form, items: [...form.items, newItem()] })} data-testid="invoice-add-item">
                <Plus className="w-4 h-4" /> Add Item
              </Btn>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Due Date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            <Input label="Payment Terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
            <Input label="Global Discount %" type="number" min="0" max="100" step="0.01" value={form.global_discount_pct} onChange={(e) => setForm({ ...form, global_discount_pct: e.target.value })} />
            <div className="md:col-span-3">
              <Textarea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {!editing && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.issue_immediately}
                  onChange={(e) => setForm({ ...form, issue_immediately: e.target.checked })}
                  data-testid="invoice-issue-immediately"
                />
                Issue immediately (finalize on create)
              </label>
            )}
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/60 rounded-md p-4 text-sm">
            <div className="flex justify-between"><span>Subtotal (before discount)</span><span className="font-mono">{fmtMoney(totals.subBefore)} EGP</span></div>
            <div className="flex justify-between"><span>Global Discount</span><span className="font-mono">- {fmtMoney(totals.gdAmt)} EGP</span></div>
            <div className="flex justify-between"><span>Subtotal (after discount)</span><span className="font-mono">{fmtMoney(totals.subAfter)} EGP</span></div>
            <div className="flex justify-between"><span>VAT Total</span><span className="font-mono">{fmtMoney(totals.vat)} EGP</span></div>
            <div className="flex justify-between font-bold text-base mt-2 pt-2 border-t border-slate-300 dark:border-slate-600">
              <span>Grand Total</span><span className="font-mono text-blue-700 dark:text-blue-300">{fmtMoney(totals.grand)} EGP</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="invoice-submit">{busy ? "Saving..." : (editing ? "Update Invoice" : "Create Invoice")}</Btn>
          </div>
        </form>
      </Modal>

      {/* View modal */}
      <Modal open={!!viewOpen} onClose={() => setViewOpen(null)} title={viewOpen?.number || "Invoice"} maxWidth="max-w-3xl">
        {viewOpen && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><div className="text-xs text-slate-500">Customer</div><div className="font-medium">{viewOpen.customer_name}</div></div>
              <div><div className="text-xs text-slate-500">Type</div><div>{viewOpen.customer_type}</div></div>
              <div><div className="text-xs text-slate-500">Tax ID</div><div>{viewOpen.customer_tax_id || "-"}</div></div>
              <div><div className="text-xs text-slate-500">Status</div><Badge color={STATUS_COLOR[viewOpen.status]}>{viewOpen.status}</Badge></div>
              <div><div className="text-xs text-slate-500">Issued</div><div>{viewOpen.issued_at ? fmtDate(viewOpen.issued_at) : "-"}</div></div>
              <div><div className="text-xs text-slate-500">Due</div><div>{viewOpen.due_date || "-"}</div></div>
              <div><div className="text-xs text-slate-500">Grand Total</div><div className="font-bold">{fmtMoney(viewOpen.grand_total)} EGP</div></div>
              <div><div className="text-xs text-slate-500">Paid</div><div className="font-bold text-emerald-600">{fmtMoney(viewOpen.paid_amount || 0)} EGP</div></div>
            </div>
            {viewOpen.amount_in_words && (
              <div className="text-xs italic text-slate-500 border-l-2 border-blue-300 ps-2">{viewOpen.amount_in_words}</div>
            )}
            <div className="rounded-md ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr><th className="px-2 py-1.5 text-start">Item</th><th className="px-2 py-1.5 text-end">Qty</th><th className="px-2 py-1.5 text-end">Unit</th><th className="px-2 py-1.5 text-end">VAT</th><th className="px-2 py-1.5 text-end">Total</th></tr>
                </thead>
                <tbody>
                  {viewOpen.items.map((it, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-2 py-1.5">{it.product_name}</td>
                      <td className="px-2 py-1.5 text-end">{it.quantity}</td>
                      <td className="px-2 py-1.5 text-end">{fmtMoney(it.unit_price)}</td>
                      <td className="px-2 py-1.5 text-end">{fmtMoney(it.line_vat)}</td>
                      <td className="px-2 py-1.5 text-end font-semibold">{fmtMoney(it.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <Btn onClick={() => openPdf(viewOpen)}><Printer className="w-4 h-4" /> Open PDF</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel modal */}
      <Modal open={!!cancelOpen} onClose={() => setCancelOpen(null)} title={`Cancel Invoice ${cancelOpen?.number || ""}`} maxWidth="max-w-md">
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">Cancellation reason is required by law and will be recorded in the audit log.</p>
          <Textarea label="Reason *" rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="invoice-cancel-reason" />
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setCancelOpen(null)}>Close</Btn>
            <Btn variant="danger" onClick={submitCancel} data-testid="invoice-cancel-submit">Cancel Invoice</Btn>
          </div>
        </div>
      </Modal>

      {/* Payment modal */}
      <Modal open={!!payOpen} onClose={() => setPayOpen(null)} title={`Record Payment - ${payOpen?.number || ""}`} maxWidth="max-w-md">
        {payOpen && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-slate-50 dark:bg-slate-800 p-2 rounded"><div className="text-xs text-slate-500">Total</div><div className="font-bold">{fmtMoney(payOpen.grand_total)} EGP</div></div>
              <div className="bg-emerald-50 dark:bg-emerald-950/40 p-2 rounded"><div className="text-xs text-slate-500">Already Paid</div><div className="font-bold text-emerald-700">{fmtMoney(payOpen.paid_amount || 0)} EGP</div></div>
              <div className="col-span-2 bg-amber-50 dark:bg-amber-950/40 p-2 rounded"><div className="text-xs text-slate-500">Remaining</div><div className="font-bold text-amber-700">{fmtMoney(payOpen.grand_total - (payOpen.paid_amount || 0))} EGP</div></div>
            </div>
            <Input label="Payment Amount (EGP)" type="number" min="0" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="invoice-pay-amount" />
            <div className="flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setPayOpen(null)}>Close</Btn>
              <Btn onClick={submitPayment} data-testid="invoice-pay-submit">Record Payment</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
